import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import Pagination from '../../components/Pagination';
import {
  MEMBER_ROLE_LABELS,
  MEMBER_ROLE_OPTIONS,
  TIMED_ORDER_STATUS_LABELS,
  TIMED_ORDER_STATUS_OPTIONS,
  TIMED_ORDER_TREND_LABELS,
  TIMED_ORDER_TREND_OPTIONS,
  TIMED_PROFIT_LOSS_LABELS,
  TIMED_PROFIT_LOSS_OPTIONS,
  buildTimedOrderPageParams,
  defaultTimedOrderTimeRange,
  formatAmount,
  formatPhoneEmail,
  formatPrice,
  validateTimedOrderTimeRange,
} from './timedOrderConstants';
import {
  formatSignedProfitLoss,
  orderTrendClassName,
  profitLossStatusClassName,
} from '../../utils/orderDisplayFormat';

const DEFAULT_RANGE = defaultTimedOrderTimeRange();

const EMPTY_FILTERS = {
  status: '',
  roleId: '',
  orderTrend: '',
  profitLossStatus: '',
  orderSerialId: '',
  memberIdsCsv: '',
  phoneEmail: '',
  inviterId: '',
  orderTimeFromInput: DEFAULT_RANGE.orderTimeFromInput,
  orderTimeToInput: DEFAULT_RANGE.orderTimeToInput,
};

function formatDurationSeconds(seconds) {
  if (seconds == null || seconds === '') {
    return '—';
  }
  return `${seconds}s`;
}

function formatPayoutOdds(odds) {
  if (odds == null || odds === '') {
    return '—';
  }
  const num = Number(odds);
  if (Number.isNaN(num)) {
    return '—';
  }
  return `${(num * 100).toFixed(0)}%`;
}

export default function TimedOrdersPage() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  async function loadRecords(pageNo = 1, nextFilters = filters) {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.timedOrdersPage(buildTimedOrderPageParams(pageNo, nextFilters));
      setRecords(data?.records || []);
      setPage(data?.current || pageNo);
      setTotalPages(data?.pages || 1);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRecords(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleSearch(event) {
    event.preventDefault();
    const rangeError = validateTimedOrderTimeRange(
      filters.orderTimeFromInput,
      filters.orderTimeToInput
    );
    if (rangeError) {
      setError(rangeError);
      return;
    }
    loadRecords(1, filters);
  }

  function handleReset() {
    const reset = { ...EMPTY_FILTERS };
    setFilters(reset);
    loadRecords(1, reset);
  }

  async function handleExport(exportCurrentPageOnly) {
    const rangeError = validateTimedOrderTimeRange(
      filters.orderTimeFromInput,
      filters.orderTimeToInput
    );
    if (rangeError) {
      setError(rangeError);
      return;
    }
    setExporting(true);
    setError('');
    try {
      const params = {
        ...buildTimedOrderPageParams(page, filters),
        exportCurrentPageOnly,
        fileName: 'timed_order',
      };
      await adminApi.exportTimedOrdersExcel(params);
    } catch (err) {
      setError(err.message || '匯出失敗');
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="page-card">
      <h2>限時交易</h2>

      <form className="filter-bar filter-bar-wrap" onSubmit={handleSearch}>
        <div className="form-field">
          <label htmlFor="timedOrderStatus">訂單狀態</label>
          <select
            id="timedOrderStatus"
            value={filters.status}
            onChange={(e) => updateFilter('status', e.target.value)}
          >
            {TIMED_ORDER_STATUS_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="timedRoleId">帳號類型</label>
          <select
            id="timedRoleId"
            value={filters.roleId}
            onChange={(e) => updateFilter('roleId', e.target.value)}
          >
            {MEMBER_ROLE_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="timedOrderTrend">投注類型</label>
          <select
            id="timedOrderTrend"
            value={filters.orderTrend}
            onChange={(e) => updateFilter('orderTrend', e.target.value)}
          >
            {TIMED_ORDER_TREND_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="timedProfitLossStatus">盈虧狀態</label>
          <select
            id="timedProfitLossStatus"
            value={filters.profitLossStatus}
            onChange={(e) => updateFilter('profitLossStatus', e.target.value)}
          >
            {TIMED_PROFIT_LOSS_OPTIONS.map((item) => (
              <option key={item.value || 'all'} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label htmlFor="timedOrderSerialId">訂單 ID</label>
          <input
            id="timedOrderSerialId"
            value={filters.orderSerialId}
            onChange={(e) => updateFilter('orderSerialId', e.target.value)}
            placeholder="請輸入訂單 ID"
          />
        </div>

        <div className="form-field">
          <label htmlFor="timedMemberIds">用戶 ID</label>
          <input
            id="timedMemberIds"
            value={filters.memberIdsCsv}
            onChange={(e) => updateFilter('memberIdsCsv', e.target.value)}
            placeholder="請輸入用戶 ID"
          />
        </div>

        <div className="form-field">
          <label htmlFor="timedPhoneEmail">用戶郵箱/手機號</label>
          <input
            id="timedPhoneEmail"
            value={filters.phoneEmail}
            onChange={(e) => updateFilter('phoneEmail', e.target.value)}
            placeholder="請輸入用戶郵箱/手機號"
          />
        </div>

        <div className="form-field">
          <label htmlFor="timedInviterId">直屬上級用戶 ID</label>
          <input
            id="timedInviterId"
            value={filters.inviterId}
            onChange={(e) => updateFilter('inviterId', e.target.value)}
            placeholder="請輸入直屬上級用戶 ID"
          />
        </div>

        <div className="form-field">
          <label htmlFor="timedOrderTimeFrom">下單時間起</label>
          <input
            id="timedOrderTimeFrom"
            type="datetime-local"
            value={filters.orderTimeFromInput}
            onChange={(e) => updateFilter('orderTimeFromInput', e.target.value)}
          />
        </div>

        <div className="form-field">
          <label htmlFor="timedOrderTimeTo">下單時間迄</label>
          <input
            id="timedOrderTimeTo"
            type="datetime-local"
            value={filters.orderTimeToInput}
            onChange={(e) => updateFilter('orderTimeToInput', e.target.value)}
          />
        </div>

        <div className="filter-actions">
          <button type="submit" className="btn btn-primary">
            搜索
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleReset}>
            重置
          </button>
          <button
            type="button"
            className="btn btn-success"
            disabled={exporting || loading}
            onClick={() => handleExport(true)}
          >
            {exporting ? '匯出中…' : '匯出 Excel（當前頁面）'}
          </button>
          <button
            type="button"
            className="btn btn-success"
            disabled={exporting || loading}
            onClick={() => handleExport(false)}
          >
            {exporting ? '匯出中…' : '匯出 Excel（全部資料）'}
          </button>
        </div>
      </form>

      {loading && <p>載入中…</p>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="table-scroll">
        <table className="data-table data-table--wide">
          <thead>
            <tr>
              <th>訂單 ID</th>
              <th>訂單狀態</th>
              <th>帳號類型</th>
              <th>用戶 ID</th>
              <th>用戶郵箱/手機號</th>
              <th>直屬上級用戶 ID</th>
              <th>交易對</th>
              <th>結算時間</th>
              <th>賠率</th>
              <th>投注類型</th>
              <th>買入價</th>
              <th>成交價</th>
              <th>下單金額</th>
              <th>手續費</th>
              <th>盈虧金額</th>
              <th>盈虧狀態</th>
              <th>交易前帳戶餘額</th>
              <th>交易後帳戶餘額</th>
              <th>下單時間</th>
              <th>結算時間</th>
            </tr>
          </thead>
          <tbody>
            {!loading && records.length === 0 ? (
              <tr>
                <td colSpan={20} className="empty-cell">
                  尚無紀錄
                </td>
              </tr>
            ) : (
              records.map((row) => {
                const profitLoss = formatSignedProfitLoss(row.profitLoss);
                const trendLabel =
                  TIMED_ORDER_TREND_LABELS[row.orderTrend] || row.orderTrend || '—';
                const profitLossStatusLabel =
                  TIMED_PROFIT_LOSS_LABELS[row.profitLossStatus] ||
                  row.profitLossStatus ||
                  '—';

                return (
                <tr key={row.id || row.orderSerialId}>
                  <td className="mono-cell">{row.orderSerialId || '—'}</td>
                  <td>
                    {TIMED_ORDER_STATUS_LABELS[row.status] || row.statusLabel || row.status || '—'}
                  </td>
                  <td>{MEMBER_ROLE_LABELS[row.roleId] || row.roleId || '—'}</td>
                  <td>{row.memberId ?? '—'}</td>
                  <td>{formatPhoneEmail(row)}</td>
                  <td>{row.inviterId ?? '—'}</td>
                  <td>{row.tradingPair || row.marketSymbolCode || '—'}</td>
                  <td>{formatDurationSeconds(row.durationSeconds)}</td>
                  <td>{formatPayoutOdds(row.payoutOdds)}</td>
                  <td>
                    <span className={orderTrendClassName(row.orderTrend)}>{trendLabel}</span>
                  </td>
                  <td>{formatPrice(row.orderPrice, 6)}</td>
                  <td>{formatPrice(row.settlementPrice, 6)}</td>
                  <td>{formatAmount(row.betAmount)}</td>
                  <td>{formatAmount(row.fee)}</td>
                  <td className={profitLoss.className}>{profitLoss.text}</td>
                  <td>
                    <span className={profitLossStatusClassName(row.profitLossStatus)}>
                      {profitLossStatusLabel}
                    </span>
                  </td>
                  <td>{formatAmount(row.beforeBalance)}</td>
                  <td>{formatAmount(row.balance)}</td>
                  <td>{formatDateTime(row.createTime)}</td>
                  <td>{formatDateTime(row.settleTime)}</td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={(p) => loadRecords(p, filters)} />
    </div>
  );
}
